import React from 'react';
import { Box, Chip, Tooltip, Typography } from '@mui/material';

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function fmtEntryDate(dateStr) {
  if (!dateStr) return null;
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

function getMonthGrid(anchorDate) {
  const year = anchorDate.getFullYear();
  const month = anchorDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  // Start from Monday of the week containing the 1st
  const startDow = firstDay.getDay(); // 0=Sun
  const offset = startDow === 0 ? 6 : startDow - 1;
  const start = new Date(firstDay);
  start.setDate(firstDay.getDate() - offset);

  const cells = [];
  const cur = new Date(start);
  while (cur <= lastDay || cells.length % 7 !== 0) {
    cells.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
    if (cells.length > 42) break;
  }
  return { cells, month, year };
}

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function TTMonthView({ currentDate, entries, projectColor, onDayClick }) {
  const { cells, month } = getMonthGrid(currentDate);
  const today = new Date();

  return (
    <Box>
      {/* Day-of-week header */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', mb: 0.5 }}>
        {DAY_LABELS.map(d => (
          <Typography key={d} sx={{ fontSize: 11, fontWeight: 700, color: '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.5, textAlign: 'center', pb: 1 }}>
            {d}
          </Typography>
        ))}
      </Box>

      {/* Grid */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
        {cells.map((day, i) => {
          const inMonth = day.getMonth() === month;
          const isToday = isSameDay(day, today);
          const dayEntries = entries.filter(e => {
            const ed = e.entryDate ? fmtEntryDate(e.entryDate) : null;
            return ed && isSameDay(ed, day);
          });
          const totalHours = dayEntries.reduce((s, e) => s + (e.workingHours || 0), 0);

          // Color intensity based on hours
          const intensity = totalHours === 0 ? 0 : totalHours >= 8 ? 1 : totalHours / 8;
          const bgBlue = intensity > 0 ? `rgba(66,120,152,${0.06 + intensity * 0.14})` : undefined;

          // Up to 2 project chips
          const uniqueProjects = [...new Map(dayEntries.map(e => [e.projectId, e])).values()];

          return (
            <Box
              key={i}
              onClick={() => onDayClick(day)}
              sx={{
                minHeight: 80,
                p: 0.75,
                borderRadius: 1.5,
                border: '1px solid',
                borderColor: isToday ? '#427898' : '#EEF2F6',
                bgcolor: isToday ? '#EFF6FF' : bgBlue || '#fff',
                cursor: 'pointer',
                opacity: inMonth ? 1 : 0.3,
                transition: 'all 0.15s',
                '&:hover': { borderColor: '#427898', bgcolor: '#F8FBFF', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{
                  fontSize: 13, fontWeight: isToday ? 800 : 500,
                  color: isToday ? '#427898' : inMonth ? '#0F172A' : '#94A3B8',
                  width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  borderRadius: '50%',
                  bgcolor: isToday ? '#DBEAFE' : 'transparent',
                }}>
                  {day.getDate()}
                </Typography>
                {totalHours > 0 && (
                  <Typography sx={{ fontSize: 10, fontWeight: 700, color: totalHours >= 8 ? '#16A34A' : '#F59E0B' }}>
                    {totalHours % 1 === 0 ? totalHours : totalHours.toFixed(1)}h
                  </Typography>
                )}
              </Box>

              {/* Project chips */}
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                {uniqueProjects.slice(0, 2).map(e => (
                  <Tooltip key={e.projectId} title={e.projectName}>
                    <Box sx={{
                      height: 4, borderRadius: 4,
                      bgcolor: projectColor(e.projectId),
                      opacity: 0.7,
                    }} />
                  </Tooltip>
                ))}
                {uniqueProjects.length > 2 && (
                  <Typography sx={{ fontSize: 9, color: '#94A3B8' }}>+{uniqueProjects.length - 2} more</Typography>
                )}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
